/**
 * Incident Card Agent – Dubai Police 999 Emergency Assistant
 *
 * Dedicated step for incident confirmation and calming reassurance.
 *
 * On activation:
 *   1. Emits SHOW_INCIDENT_CARD_EVENT so AltairAgent renders the card UI
 *   2. The LLM reads the incident number aloud and says the calming message
 *   3. Waits for user to press OK or say OK
 *   4. Calls proceed_to_closing → routes to closing_agent
 *
 * Labels are NEVER cleared on transition.
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';

export const SHOW_INCIDENT_CARD_EVENT = 'dp:showIncidentCard';
export const SESSION_ENDED_EVENT = 'dp:sessionEnded';

export function createIncidentCardAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'incident_card_agent',
    name: 'Incident Card Agent',
    description: 'Shows incident card, reads number, delivers calming message, waits for OK, then routes to closing',

    initialPrompt: 'Read the incident number from context aloud in the user\'s language. Say the calming message. Wait for the user to say OK or acknowledge. Then call proceed_to_closing().',

    entryGuardrails: requireLanguage(),

    exitGuardrails: {
      transitions: new Map([
        ['to_closing', { target: { agentId: 'closing_agent' }, guardrails: [languageDetectedGuardrail] }],
      ]),
      fallback: { agentId: 'closing_agent' },
    },

    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {

      if (toolName === 'proceed_to_closing') {
        const confirmedAgencies = context.transitionData.confirmedAgencies || [];
        console.log('[IncidentCardAgent] User acknowledged — routing to closing. Agencies preserved:', confirmedAgencies);

        return {
          toolName, args,
          result: { success: true, message: 'Routing to closing agent' },
          triggersTransition: true,
          transitionTo: 'closing_agent',
          transitionData: {
            confirmedAgencies,
            incidentSummary: context.transitionData.incidentSummary || '',
          },
        };
      }

      return { toolName, args, result: { success: false, error: `Unknown tool: ${toolName}` } };
    },

    onActivate: async (context: AgentContext) => {
      const confirmedAgencies = context.transitionData.confirmedAgencies || [];
      const incidentNumber = localStorage.getItem('dp_incident_number') || '';

      console.log('[IncidentCardAgent] Activated – incident #:', incidentNumber, '| agencies:', confirmedAgencies);

      // Emit the incident card event so AltairAgent renders the card UI
      if (incidentNumber || confirmedAgencies.length > 0) {
        window.dispatchEvent(new CustomEvent(SHOW_INCIDENT_CARD_EVENT, {
          detail: { incidentNumber, agencies: confirmedAgencies },
        }));
        console.log('[IncidentCardAgent] Emitted SHOW_INCIDENT_CARD_EVENT');
      }
    },
  };
}

export default createIncidentCardAgent;
